import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { COMPANY, LOGO } from "@/lib/constants";

const HERO = "https://images.unsplash.com/photo-1540476547779-348beb642680?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/agent"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email.trim(), password);
      nav(u.role === "admin" ? "/admin" : "/agent");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-ink text-slate-100 grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
        <div className="absolute bottom-0 p-12 rise">
          <p className="font-mono text-xs tracking-widest text-brand-sky mb-4">FIELD · INSPECT · MEASURE</p>
          <h2 className="font-heading text-4xl font-extrabold leading-tight max-w-md">كل مقاس موثّق. كل تركيب بدون أخطاء.</h2>
          <p className="text-slate-400 mt-4 max-w-md">من إسناد المعاينة إلى كشف المقاسات النهائي للورشة، في مسار واحد واضح.</p>
        </div>
      </div>
      <div className="grid-bg flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="flex items-center gap-3 mb-10">
            <img src={LOGO} alt={COMPANY} data-testid="company-logo" className="h-16 w-16 rounded-full bg-white object-contain ring-2 ring-brand-red/60" />
            <div>
              <p className="font-heading font-extrabold text-lg" data-testid="company-name">{COMPANY}</p>
              <p className="text-xs text-slate-400">نظام معاينات المناديب</p>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-extrabold mb-2">تسجيل الدخول</h1>
          <p className="text-slate-400 text-sm mb-8">للإدارة والمناديب. حسابات المناديب تُنشأ من لوحة الإدارة.</p>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">رقم الجوال أو البريد الإلكتروني</Label>
              <Input id="email" dir="ltr" type="text" autoComplete="username" placeholder="05xxxxxxxx" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-panel border-line text-left" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">كلمة المرور</Label>
              <div className="relative" dir="ltr">
                <Input id="password" dir="ltr" type={show ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-panel border-line text-left pr-12" data-testid="login-password-input" />
                <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"} title={show ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                  data-testid="login-toggle-password" className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-panel-hover transition-colors">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full h-12 rounded-lg bg-brand-red text-white font-bold flex items-center justify-center gap-2 hover:bg-brand-red-dark transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>دخول <ArrowLeft size={18} /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
